import { useMemo } from "react";

import type { ManageFeaturesI } from "@/api/routes/manage";
import { useRewardWhatsNewCards } from "@/modules/rewards/data/use-reward-whats-new-cards";
import { useTopTenWhatsNewCards } from "@/modules/top-ten/whats-new/use-top-ten-whats-new-cards";
import { useDailyTodosWhatsNewCards } from "@/modules/daily-todos/whats-new/use-daily-todos-whats-new-cards";

import { WHATS_NEW_ENTRIES } from "../entries";
import { resolveWhatsNewEntry } from "../lib/audience";
import type {
  WhatsNewAudienceT,
  WhatsNewCardI,
  WhatsNewEntryI,
} from "../lib/types";

/**
 * Where "What's new" cards come from. Each source is a hook that resolves
 * its cards for the viewer's audience; the module waits until every source
 * has settled, then decides once what to show.
 *
 * Adding a source (C-16 S1's running incentives, or a backend feed later):
 * write `useXxxWhatsNewCards(audience)` returning `WhatsNewSourceI`, call it
 * below and add it to the list. Feature-detect the route (404 + 1035 → no
 * cards, settled) and settle on errors too, so a failing source never holds
 * the others back. Give each card a stable id that changes when the
 * announcement changes (e.g. `incentive:<id>:<updated_at>`).
 */

export interface WhatsNewSourceI {
  cards: Array<WhatsNewCardI>;
  /** False while the source is still loading. */
  isSettled: boolean;
}

/**
 * The static entries shipped with the app, resolved for `audience`. An
 * entry tied to a feature waits until `manage/me` says it is on.
 */
export const resolveStaticWhatsNewCards = (
  entries: Array<WhatsNewEntryI>,
  audience: WhatsNewAudienceT,
  features?: Partial<ManageFeaturesI>
): Array<WhatsNewCardI> =>
  entries
    .filter((entry) => !entry.feature || features?.[entry.feature] === true)
    .map((entry) => resolveWhatsNewEntry(entry, audience))
    .filter((card): card is WhatsNewCardI => card !== null);

/** All sources as one: every card once (first source wins), settled when all are. */
export const mergeWhatsNewSources = (
  sources: Array<WhatsNewSourceI>
): WhatsNewSourceI => {
  const seenIds = new Set<string>();
  const cards: Array<WhatsNewCardI> = [];

  sources.forEach((source) => {
    source.cards.forEach((card) => {
      if (!seenIds.has(card.id)) {
        seenIds.add(card.id);
        cards.push(card);
      }
    });
  });

  return { cards, isSettled: sources.every((source) => source.isSettled) };
};

const useStaticWhatsNewCards = (
  audience: WhatsNewAudienceT | null,
  features?: Partial<ManageFeaturesI>
): WhatsNewSourceI =>
  useMemo(
    () => ({
      cards: audience
        ? resolveStaticWhatsNewCards(WHATS_NEW_ENTRIES, audience, features)
        : [],
      isSettled: true,
    }),
    [audience, features]
  );

/** Every card for `audience`; none (and not settled) while it is unknown. */
export const useWhatsNewCards = (
  audience: WhatsNewAudienceT | null,
  features?: Partial<ManageFeaturesI>,
  /**
   * The viewer has Leads → To-Dos (C-16 S6); undefined while unknown. Passed
   * in so this file never imports the global context.
   */
  hasDailyTodos?: boolean
): WhatsNewSourceI => {
  const staticCards = useStaticWhatsNewCards(audience, features);
  // C-16 S1: running contests, and Rewards' launch card while it is on.
  const rewardCards = useRewardWhatsNewCards(audience);
  // C-16 S4: shown only while the backend's Top 10 switch is on.
  const topTenCards = useTopTenWhatsNewCards(audience);
  // C-16 S6: shown only while the backend's To-Dos switch is on.
  const dailyTodosCards = useDailyTodosWhatsNewCards(audience, hasDailyTodos);

  return useMemo(() => {
    if (!audience) {
      return { cards: [], isSettled: false };
    }

    return mergeWhatsNewSources([
      staticCards,
      rewardCards,
      topTenCards,
      dailyTodosCards,
    ]);
  }, [audience, staticCards, rewardCards, topTenCards, dailyTodosCards]);
};
